import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Result } from '@vello/core';
import { type AnalyticsBiCatalogView, type AnalyticsBiQueryView, type AnalyticsBiTarget, type AnalyticsBiTargetInput, type ApiError } from '@vello/api-client';
import { type QueryFilter, type QueryRequest } from '@vello/analytics-semantic';
import {
  type BiAccess,
  type BiErrorView,
  type BiPanelDef,
  type ExplorerState,
  type PanelQueryContext,
  BI_EXPORT_POLICY,
  BI_TARGETS_POLICY,
  baselineEmpty,
  biErrorView,
  blockRequest,
  explorerRequest,
  insightBlockRequest,
  panelGrain,
  targetErrorView,
  grantsAllow,
  isCompareUnplaceable,
  sharedBiQueue,
} from '../../../src/index';
import { type Granularity } from '@vello/analytics-semantic';
import { useAdmin } from '../../session';

/**
 * The reads of Análises. Every call goes through the one shared queue (at most two in flight; the server answers a third with
 * 429), a late answer of an older request is dropped, and NOTHING is cached: an answer lives in component state only, a new
 * period, scope, page or drill is a new server request, and leaving the screen forgets it.
 */
export type Loadable<T> = { readonly status: 'loading' } | { readonly status: 'error'; readonly error: BiErrorView } | { readonly status: 'ready'; readonly value: T };

export interface QueryAnswer {
  readonly view: AnalyticsBiQueryView;
  /** The automatic comparison fell back to the previous period (the year before had nothing to compare to). */
  readonly fallback: boolean;
}

type Ask = (request: QueryRequest) => Promise<Result<AnalyticsBiQueryView, ApiError> | undefined>;

/**
 * Ask with the automatic comparison: the year-before weekday match first; if the planner cannot place it or it has no figures, the
 * previous period (once). A fixed comparison is asked as it is. `undefined` = the caller moved on (stale).
 */
async function askAuto(ask: Ask, build: (fallback: boolean) => QueryRequest, auto: boolean, known?: boolean): Promise<{ result: Result<AnalyticsBiQueryView, ApiError>; fallback: boolean } | undefined> {
  const first = await ask(build(known === true));
  if (first === undefined) return undefined;
  if (!auto || known !== undefined) return { result: first, fallback: known === true };
  const refused = !first.ok && isCompareUnplaceable(first.error);
  const empty = first.ok && baselineEmpty(first.value);
  if (!refused && !empty) return { result: first, fallback: false };
  const second = await ask(build(true));
  if (second === undefined) return undefined;
  if (second.ok) return { result: second, fallback: true };
  return { result: refused ? second : first, fallback: false };
}

export function useBiCatalog(): { readonly state: Loadable<AnalyticsBiCatalogView>; readonly reload: () => void } {
  const { runtime } = useAdmin();
  const [state, setState] = useState<Loadable<AnalyticsBiCatalogView>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void runtime.analyticsBi.catalog().then((res) => {
      if (!live) return;
      setState(res.ok ? { status: 'ready', value: res.value } : { status: 'error', error: biErrorView(res.error) });
    });
    return () => {
      live = false;
    };
  }, [runtime, attempt]);
  return { state, reload: useCallback(() => setAttempt((n) => n + 1), []) };
}

/** What the caller may do beyond reading: see people (the catalog is the authority) and export (the grant is). */
export function useBiAccess(catalog: AnalyticsBiCatalogView | undefined): BiAccess & { readonly canExport: boolean } {
  const { grants } = useAdmin();
  return useMemo(
    () => ({
      personal: catalog?.dimensions.some((d) => d.personal) ?? false,
      canExport: grants !== undefined && grantsAllow(grants, BI_EXPORT_POLICY),
    }),
    [catalog, grants],
  );
}

function useAsk(): { ask: Ask; generation: React.MutableRefObject<number> } {
  const { runtime } = useAdmin();
  const generation = useRef(0);
  const ask = useCallback<Ask>(
    (request) => {
      const mine = generation.current;
      return sharedBiQueue.run(() => runtime.analyticsBi.query(request), () => mine !== generation.current);
    },
    [runtime],
  );
  return { ask, generation };
}

export interface PanelData {
  readonly tiles: Readonly<Record<string, Loadable<AnalyticsBiQueryView>>>;
  readonly fallback: boolean;
  readonly reload: () => void;
  /** The background re-read (`useAutoRefresh`): same questions, but every tile keeps its current answer until the new one lands, and a failed tick keeps the old answer. */
  readonly refresh: () => void;
}

/**
 * Load a panel: the first KPI block decides the automatic comparison, then every other block is queued (two at a time). Each tile
 * has its own loading / error / answer, so one refused tile never blanks the page.
 */
export function usePanelData(panel: BiPanelDef, ctx: PanelQueryContext, grain: Granularity, enabled: boolean): PanelData {
  const { ask, generation } = useAsk();
  const [tiles, setTiles] = useState<Record<string, Loadable<AnalyticsBiQueryView>>>({});
  const [fallback, setFallback] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const quiet = useRef(false);
  const key = JSON.stringify([panel.id, ctx.period, ctx.compare, ctx.upToNowAgainst, ctx.scope, ctx.bar, grain, enabled, attempt]);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    if (!enabled) return undefined;
    const mine = ++generation.current;
    const c = ctxRef.current;
    const auto = c.compare === 'auto';
    const drawn = panelGrain(panel, grain);
    const blocks: { id: string; build: (fb: boolean) => QueryRequest }[] = [
      ...panel.kpis.map((b, i) => ({ id: `kpi:${i}`, build: (fb: boolean) => blockRequest(b, { ...c, fallback: fb }) })),
      { id: 'trend', build: (fb: boolean) => blockRequest(panel.trend.block, { ...c, fallback: fb }, { granularity: drawn }) },
      ...panel.breakdowns.map((b) => ({ id: `break:${b.id}`, build: (fb: boolean) => blockRequest(b.block, { ...c, fallback: fb }, { ignoreBar: b.dimension }) })),
      // D531: the insights are queued after the headline blocks, two at a time like the rest
      ...(panel.insights ?? []).flatMap((i) => i.blocks.map((b) => ({ id: `ins:${i.id}:${b.id}`, build: (fb: boolean) => insightBlockRequest(b, { ...c, fallback: fb }, drawn) }))),
    ];
    // A background tick keeps what is drawn (no skeleton flash); any other change of the question starts from "loading".
    const background = quiet.current;
    quiet.current = false;
    if (!background) {
      setTiles(Object.fromEntries(blocks.map((b) => [b.id, { status: 'loading' } as const])));
      setFallback(false);
    }
    const settle = (id: string, result: Result<AnalyticsBiQueryView, ApiError>): void => {
      if (mine !== generation.current) return;
      setTiles((t) => {
        if (background && !result.ok && t[id]?.status === 'ready') return t;
        return { ...t, [id]: result.ok ? { status: 'ready', value: result.value } : { status: 'error', error: biErrorView(result.error) } };
      });
    };
    void (async () => {
      const [probe, ...rest] = blocks;
      const first = await askAuto(ask, probe!.build, auto);
      if (first === undefined || mine !== generation.current) return;
      setFallback(first.fallback);
      settle(probe!.id, first.result);
      await Promise.all(
        rest.map(async (b) => {
          const got = await askAuto(ask, b.build, auto, first.fallback);
          if (got !== undefined) settle(b.id, got.result);
        }),
      );
    })();
    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` carries every input
  }, [key, ask, generation, panel]);

  return {
    tiles,
    fallback,
    reload: useCallback(() => setAttempt((n) => n + 1), []),
    refresh: useCallback(() => {
      quiet.current = true;
      setAttempt((n) => n + 1);
    }, []),
  };
}

/** Run the explorer’s applied state. `undefined` holds the read back (nothing applied yet, or the state cannot be asked). */
export function useExplorerData(applied: ExplorerState | undefined, scope: readonly QueryFilter[], nonce: number): { readonly state: Loadable<QueryAnswer> | undefined; readonly reload: () => void } {
  const { ask, generation } = useAsk();
  const [state, setState] = useState<Loadable<QueryAnswer> | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([applied, scope, nonce, attempt]);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  useEffect(() => {
    const current = appliedRef.current;
    if (current === undefined) {
      setState(undefined);
      return undefined;
    }
    const mine = ++generation.current;
    setState({ status: 'loading' });
    void (async () => {
      const got = await askAuto(ask, (fb) => explorerRequest(current, scope, { fallback: fb }), current.compare === 'auto');
      if (got === undefined || mine !== generation.current) return;
      setState(got.result.ok ? { status: 'ready', value: { view: got.result.value, fallback: got.fallback } } : { status: 'error', error: biErrorView(got.result.error) });
    })();
    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` carries every input
  }, [key, ask, generation]);

  return { state, reload: useCallback(() => setAttempt((n) => n + 1), []) };
}

export interface TargetsApi {
  readonly state: Loadable<readonly AnalyticsBiTarget[]>;
  /** The operator holds `analytics:manage_targets`: the scorecards offer to set and clear a goal. */
  readonly canManage: boolean;
  readonly reload: () => void;
  readonly set: (input: AnalyticsBiTargetInput) => Promise<{ readonly ok: true } | { readonly ok: false; readonly error: BiErrorView }>;
  readonly clear: (metric: string, marketId: string) => Promise<{ readonly ok: true } | { readonly ok: false; readonly error: BiErrorView }>;
}

/** The goals (metas) the scorecards are read against (D531): read with `analytics:view`, kept by the administrators. A failed read is not fatal - the scorecards simply show no goal. */
export function useTargets(): TargetsApi {
  const { runtime, grants } = useAdmin();
  const canManage = grants !== undefined && grantsAllow(grants, BI_TARGETS_POLICY);
  const [state, setState] = useState<Loadable<readonly AnalyticsBiTarget[]>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const api = runtime.analyticsBi;
  useEffect(() => {
    let live = true;
    void api.targets().then((res) => {
      if (!live) return;
      setState(res.ok ? { status: 'ready', value: res.value.targets } : { status: 'error', error: targetErrorView(res.error) });
    });
    return () => {
      live = false;
    };
  }, [api, attempt]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const set = useCallback<TargetsApi['set']>(
    async (input) => {
      const res = await api.setTarget(input);
      if (!res.ok) return { ok: false, error: targetErrorView(res.error) };
      reload();
      return { ok: true };
    },
    [api, reload],
  );
  const clear = useCallback<TargetsApi['clear']>(
    async (metric, marketId) => {
      const res = await api.clearTarget(metric, marketId);
      if (!res.ok) return { ok: false, error: targetErrorView(res.error) };
      reload();
      return { ok: true };
    },
    [api, reload],
  );
  return { state, canManage, reload, set, clear };
}
